/**
 * Bodies of the two debounced type-ahead inputs in the Profiler search form —
 * company-name lookup and the industry-codes tree filter — extracted from the
 * store actions in SearchResults/index.jsx (TD-2746 — SonarCloud S2004).
 *
 * Both actions armed a `setTimeout` whose callback was produced by an
 * immediately-invoked arrow, so the work landed five closures deep inside the
 * `useLocalObservable` factory and every `makeRequest` callback below it tripped
 * the nesting limit of 5. The work now runs at module scope and the actions keep
 * only the debounce plumbing.
 *
 * Both take the view `state` explicitly and mutate it, mirroring the sibling
 * `getResultsHelpers.js` convention, so they are unit-testable without the store.
 */
import { runInAction } from 'mobx';
import { API_ENDPOINTS } from '../../../api/endpoints';

/**
 * Fires the debounced company-name lookup.
 *
 * `firedSearchKey` is the trimmed value captured when the timer was armed. Every
 * callback re-checks it against the live input (and that the page is still
 * mounted) so a late response can never overwrite a newer one — the original
 * guard, kept verbatim but stated once.
 */
export const runProfilerNameSearch = ({ state, firedSearchKey, makeRequest }) => {
  const isStale = () =>
    !state.isRendered || state.profilerNameSearchSearchKey.trim() !== firedSearchKey;

  runInAction(() => {
    state.profilerNameSearchIsTyping = false;
    state.profilerNameSearchIsLoading = true;
    state.profilerNameSearchLoadError = null;
  });

  makeRequest({
    endpoint: API_ENDPOINTS.GET_PROFILER_COMPANIES_BY_NAME,
    body: { searchKey: firedSearchKey },
    onSuccess: (response) => {
      if (isStale()) return;
      state.profilerNameSearchSearchKeyResults = response;
    },
    onError: (errorMessage) => {
      if (isStale()) return;
      state.profilerNameSearchLoadError = errorMessage || 'Failed to load results';
    },
    onFinally: () => {
      if (isStale()) return;
      state.profilerNameSearchIsLoading = false;
    }
  });
};

/**
 * Applies the debounced industry-codes tree filter: expands every node whose
 * name contains `firedSearchKey`, expands each of their ancestors, collapses
 * everything else, and publishes the matching values as `state.searchResults`.
 *
 * Note the key is compared untrimmed here — the industries input debounces on the
 * raw value, unlike the name search.
 */
export const applyIndustriesSearchFilter = ({ state, firedSearchKey }) => {
  if (!state.isRendered || state.profilerIndustriesSearchSearchKey !== firedSearchKey) {
    return;
  }

  runInAction(() => {
    const nodeMap = new Map(state.industryCodesFlat.map((node) => [node.value, node]));

    state.industryCodesFlat.forEach((node) => {
      node.toggled = false;
    });

    const allMatches = state.industryCodesFlat.filter((node) =>
      node.name.toLowerCase().includes(firedSearchKey.toLowerCase())
    );

    const processedParents = new Set();

    allMatches.forEach((match) => {
      if (match.children?.length) {
        match.toggled = true;
      }

      let current = match;
      while (current.parent) {
        const parent = nodeMap.get(current.parent.value);
        if (!parent) break;

        if (!processedParents.has(parent.value)) {
          parent.toggled = true;
          processedParents.add(parent.value);
        }

        current = parent;
      }
    });

    state.profilerIndustriesSearchIsTyping = false;
    state.searchResults = allMatches.map((match) => match.value);
  });
};
